// Paste-tolerant parsing for the guided WhatsApp channel setup. Customers
// routinely paste the bot's whole "access not configured ... Pairing code:
// <CODE> ... approve with: openclaw pairing approve whatsapp <CODE>" reply
// instead of just the code, so we extract the value rather than rejecting it.
// Mirrors telegramSetup.ts; kept separate because the bot's approve line names
// the channel ("approve whatsapp" vs "approve telegram").

// OpenClaw device-pairing codes are short, uppercase alphanumeric, no ambiguous
// chars (0/O/1/I) — typically 8 (e.g. "4FHTUTB8"). Range-checked 6-12 so minor
// format drift doesn't break us, while staying short enough not to collide with
// the (longer) E.164 phone number printed just above the code in the reply.
const PAIRING_BARE = /^[A-Za-z0-9]{6,12}$/;
// In a pasted whole reply, pull the code after the "Pairing code" label, or the
// "approve whatsapp <code>" command line — never the phone-number line.
const PAIRING_LABELLED = /pairing code[^A-Za-z0-9]*([A-Za-z0-9]{6,12})/i;
const PAIRING_APPROVE = /approve whatsapp\s+([A-Za-z0-9]{6,12})/i;

// Returns the normalized (upper-cased) pairing code from `input`, or null.
export const extractPairingCode = (input: string): string | null => {
  const trimmed = input.trim();
  if (PAIRING_BARE.test(trimmed)) return trimmed.toUpperCase();
  const labelled = trimmed.match(PAIRING_LABELLED);
  if (labelled && labelled[1]) return labelled[1].toUpperCase();
  const approve = trimmed.match(PAIRING_APPROVE);
  if (approve && approve[1]) return approve[1].toUpperCase();
  return null;
};

// A poller that CANNOT overlap itself.
//
// Extracted rather than written inline in the modal because the no-overlap
// property is the whole point and belongs somewhere it can be tested. The
// WhatsApp wizard used to poll on a setInterval, which fires on a fixed cadence
// whether or not the last request came back. Each of those polls made ztc-core
// shell OpenClaw's CLI onto the tenant over SSM, a 9-26s call, so requests piled
// up faster than the box could answer them and two customer instances ended up
// pegged at 100% CPU for a quarter of an hour.
//
// Here the next run is scheduled only once the previous one has SETTLED, so a
// slow backend produces a slower poll, never a deeper queue.
type TimerHandle = ReturnType<typeof setTimeout>;

export interface PollTimers {
  setTimeout: (fn: () => void, ms: number) => TimerHandle;
  clearTimeout: (handle: TimerHandle) => void;
}

export interface SerialPoller {
  start: () => void;
  stop: () => void;
}

// `tick` resolves true to keep polling, false to stop (a terminal state). It
// owns its own error policy; a tick that throws is treated as "keep going", so
// one failed request cannot silently end the poll.
export const createSerialPoller = (
  tick: () => Promise<boolean>,
  intervalMs: number,
  timers: PollTimers = {
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (handle) => clearTimeout(handle),
  },
): SerialPoller => {
  let stopped = false;
  let inFlight = false;
  let handle: TimerHandle | undefined;

  const run = async (): Promise<void> => {
    // inFlight is the guard that makes overlap impossible even if something
    // calls start() twice or a stray timer fires early.
    if (stopped || inFlight) return;
    inFlight = true;
    let again = true;
    try {
      again = await tick();
    } catch {
      again = true;
    } finally {
      inFlight = false;
    }
    if (stopped || !again) return;
    handle = timers.setTimeout(() => void run(), intervalMs);
  };

  return {
    start: (): void => {
      void run();
    },
    stop: (): void => {
      stopped = true;
      if (handle !== undefined) timers.clearTimeout(handle);
    },
  };
};
